<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'
import { mapState } from 'vuex'

import { CountryEnum } from '@/store/modules/settings/types'

import ApiJustificationSummary from './ApiJustificationSummary.vue'


export default {
  components: {
    'justification-summary': ApiJustificationSummary,
    'sancare-octicon': SancareOcticon,
  },
  props: {
    appSettings: { type: Object, required: true },
    type: { type: String, required: true },
    isLoading: { type: Boolean, default: false },
    selected: { type: Boolean, required: true },
    selection: { type: Object, default: null },
    initialDiagnosis: { type: Object, required: true },
    suggestion: { type: Object, default: null },
    healthData: { type: Object, required: true },
    suggestionJustifications: { type: Object, default: null },
    readonly: { type: Boolean, default: false },
  },
  emits: ['make-selection', 'remove-das'],
  data() {
    return {
      showJustificationSummary: false,
      CountryEnum
    }
  },
  computed: {
    ...mapState({
      pmsiCountry: (state) => state.settings.pmsi.country,
    }),
    apiJustification() {
      return this.suggestionJustifications?.[`${this.type}:${this.suggestedDiagnosis.reference}`] ?? null
    },
    suggestedDiagnosis() {
      return this.suggestion.diagnoses[0]
    },
    isWildcard() {
      return this.suggestion.isWildcard
    }
  },
  methods: {
    makeSelection() {
      this.$emit('make-selection', {
        labelType: this.type,
        labelReference: this.suggestedDiagnosis.reference,
        savedSearchIds: this.suggestedDiagnosis.staySavedSearches.map((ss) =>ss.search.id),
      })
    },
    handleHealthDataSelection(mainContent, scrollTarget = null) {
      this.$store.commit('setScrollTarget', scrollTarget)
      this.$store.commit('setMainContent', mainContent)
      if (!this.selected) {
        this.makeSelection()
      }
      this.showJustificationSummary = !this.showJustificationSummary
    },
    flagJustification(payload) {
      this.$store.dispatch('createFalseJustificationForSuggestion', payload)
    },
    unflagJustification({ justificationId, docType, docId }) {
      this.$store.dispatch('deleteFalseJustification', {
        justificationId,
        reportId: docType === 'report' ? docId : null,
        healthEntryId: docType === 'textualHealthEntry' ? docId : null,
      })
    },
    removeDAS() {
      this.$emit('remove-das', this.initialDiagnosis.id)
    },
    getSearchesIds(diagnosis) {
      return _.map(diagnosis.staySavedSearches, (ss) => ss.search.id)
    },
    isSelectedSuggestion(suggestion) {
      return Boolean(
        !_.isNull(this.selection)
          && this.selection.labelType === 'DAS'
          && this.selection.labelReference === suggestion.reference
          && _.isEqual(this.selection.savedSearchIds, _.map(suggestion.staySavedSearches, 'search.id').sort((a, b) => a - b))
      )
    },
    toggleRationals() {
      this.showJustificationSummary = !this.showJustificationSummary
      if (this.showJustificationSummary) {
        this.makeSelection()
      }
    },
  },
}
</script>

<template>
  <div
    :class="{ excluded: initialDiagnosis.excluded }"
    class="row no-gutter align-items-center diagnosis-item"
  >
    <div
      :title="initialDiagnosis.associatedDiagnosis.description"
      class="col d-flex"
    >
      <h2 class="diagnosis-reference">
        {{ initialDiagnosis.reference }}
        <sancare-octicon
          v-if="initialDiagnosis.associatedDiagnosis.description === undefined"
          v-tooltip="'Le diagnostic est inconnu de la CIM-10, il n\'est pas pris en compte dans le groupage'"
          name="alert"
          :width="15"
          :height="15"
        />
      </h2>
      <div
        v-if="'level' in initialDiagnosis.associatedDiagnosis && pmsiCountry === CountryEnum.FR"
        class="diagnosis-level ms-1"
      >
        <span v-if="!initialDiagnosis.isExcluded">{{ $t('diagnosis.level') }}. {{ initialDiagnosis.associatedDiagnosis.level }}</span>
        <span v-else>{{ $t('diagnosis.excluded') }}</span>
      </div>
    </div>
    <div
      v-if="suggestion"
      title="Voir les justifications"
      class="col-auto show-justifications"
      @click.stop="toggleRationals"
    >
      <v-dropdown
        :shown="showJustificationSummary"
        placement="left"
        class="popover-container"
        popover-class="justification-popover"
        @hide="showJustificationSummary=false"
      >
        <slot />
        <template #popper>
          <justification-summary
            :is-loading="isLoading"
            :justification="suggestedDiagnosis.canShowRational ? apiJustification : null"
            :health-data="healthData"
            :label-type="type"
            :label-reference="suggestedDiagnosis.reference"
            @select-health-data="handleHealthDataSelection"
            @flag-justification="flagJustification"
            @unflag-justification="unflagJustification"
          />
        </template>
      </v-dropdown>
      <div
        v-if="suggestion.diagnoses[0] && suggestion.diagnoses[0].canShowRational && !suggestion.isWildcard && !readonly"
        :class="{ selected: isSelectedSuggestion(suggestion.diagnoses[0]) }"
        class="btn-note icon-block"
      >
        <sancare-octicon
          name="note"
          :width="18"
          :height="18"
        />
      </div>
    </div>
    <div
      v-if="!readonly"
      title="Supprimer le diagnostic"
      class="col-auto predicted-diagnosis-remover"
      @click.stop="removeDAS()"
    >
      <sancare-octicon
        name="x"
        :width="18"
        :height="18"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
